import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  Globe,
  Users,
  Plus,
  MapPin,
  Calendar as CalendarIcon,
  BadgeCheck,
  MessageCircle,
  LogOut,
  Settings,
  User,
} from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);
  const [showMeetupDialog, setShowMeetupDialog] = useState(false);

  // Journey form state
  const [journeyForm, setJourneyForm] = useState({
    title: "",
    destination: "",
    description: "",
    start_date: null,
    end_date: null,
    max_companions: 1,
    interests: "",
  });

  // Meetup form state
  const [meetupForm, setMeetupForm] = useState({
    title: "",
    location: "",
    description: "",
    date: null,
    time: "",
    venue_type: "",
    max_attendees: 5,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [journeysRes, meetupsRes] = await Promise.all([
        axios.get(`${API}/journeys`, { withCredentials: true }),
        axios.get(`${API}/meetups`, { withCredentials: true }),
      ]);
      setJourneys(journeysRes.data);
      setMeetups(meetupsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJourney = async () => {
    try {
      const payload = {
        ...journeyForm,
        start_date: journeyForm.start_date ? format(journeyForm.start_date, "yyyy-MM-dd") : "",
        end_date: journeyForm.end_date ? format(journeyForm.end_date, "yyyy-MM-dd") : "",
        interests: journeyForm.interests.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/journeys`, payload, { withCredentials: true });
      toast.success("Journey created!");
      setShowJourneyDialog(false);
      setJourneyForm({
        title: "",
        destination: "",
        description: "",
        start_date: null,
        end_date: null,
        max_companions: 1,
        interests: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create journey");
    }
  };

  const handleCreateMeetup = async () => {
    try {
      const payload = {
        ...meetupForm,
        date: meetupForm.date ? format(meetupForm.date, "yyyy-MM-dd") : "",
      };
      await axios.post(`${API}/meetups`, payload, { withCredentials: true });
      toast.success("Meetup created!");
      setShowMeetupDialog(false);
      setMeetupForm({
        title: "",
        location: "",
        description: "",
        date: null,
        time: "",
        venue_type: "",
        max_attendees: 5,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meetup");
    }
  };

  const JourneyCard = ({ journey }) => (
    <div
      className="card-luxury group cursor-pointer"
      onClick={() => navigate(`/profile/${journey.user_id}`)}
      data-testid={`journey-card-${journey.journey_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {journey.user_picture ? (
            <img
              src={journey.user_picture}
              alt={journey.user_name}
              className="w-10 h-10 object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-secondary flex items-center justify-center">
              <User className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-charcoal">{journey.user_name}</span>
              {journey.is_verified && (
                <BadgeCheck className="w-4 h-4 text-gold" data-testid="verified-badge" />
              )}
            </div>
            <p className="text-caption">{journey.destination}</p>
          </div>
        </div>
        <Globe className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <h3 className="text-h3 text-charcoal mb-2">{journey.title}</h3>
      <p className="text-body text-muted-foreground mb-4 line-clamp-2">
        {journey.description}
      </p>

      <div className="flex items-center gap-4 text-caption">
        <div className="flex items-center gap-1">
          <CalendarIcon className="w-3 h-3" />
          <span>{journey.start_date} - {journey.end_date}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          <span>{journey.max_companions} spots</span>
        </div>
      </div>

      {journey.interests?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {journey.interests.slice(0, 3).map((interest, idx) => (
            <span key={idx} className="px-2 py-1 bg-secondary text-xs font-mono uppercase">
              {interest}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const MeetupCard = ({ meetup }) => (
    <div
      className="card-luxury group cursor-pointer"
      onClick={() => navigate(`/profile/${meetup.user_id}`)}
      data-testid={`meetup-card-${meetup.meetup_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {meetup.user_picture ? (
            <img
              src={meetup.user_picture}
              alt={meetup.user_name}
              className="w-10 h-10 object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-secondary flex items-center justify-center">
              <User className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-charcoal">{meetup.user_name}</span>
              {meetup.is_verified && (
                <BadgeCheck className="w-4 h-4 text-gold" data-testid="verified-badge" />
              )}
            </div>
            <p className="text-caption">{meetup.location}</p>
          </div>
        </div>
        <Users className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <h3 className="text-h3 text-charcoal mb-2">{meetup.title}</h3>
      <p className="text-body text-muted-foreground mb-4 line-clamp-2">
        {meetup.description}
      </p>

      <div className="flex items-center gap-4 text-caption">
        <div className="flex items-center gap-1">
          <CalendarIcon className="w-3 h-3" />
          <span>{meetup.date} at {meetup.time}</span>
        </div>
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span>{meetup.venue_type}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="border-b border-border bg-ivory/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container-luxury flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/dashboard" />

          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/messages")}
              className="nav-link flex items-center gap-2"
              data-testid="nav-messages"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden md:inline">Messages</span>
            </button>

            {user?.is_admin && (
              <button
                onClick={() => navigate("/admin")}
                className="nav-link flex items-center gap-2"
                data-testid="nav-admin"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden md:inline">Admin</span>
              </button>
            )}

            <button
              onClick={() => navigate(`/profile/${user?.user_id}`)}
              className="flex items-center gap-2"
              data-testid="nav-profile"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 object-cover" />
              ) : (
                <div className="w-8 h-8 bg-secondary flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
              )}
            </button>

            <Button variant="ghost" onClick={logout} className="btn-ghost" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-luxury py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <p className="text-caption mb-2">Welcome back,</p>
          <h1 className="text-h1 text-charcoal flex items-center gap-3">
            {user?.name}
            {user?.is_verified && (
              <BadgeCheck className="w-8 h-8 text-gold" data-testid="user-verified-badge" />
            )}
          </h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="journeys" className="w-full">
          <div className="flex items-center justify-between mb-8">
            <TabsList className="bg-transparent border-b border-border rounded-none p-0 h-auto">
              <TabsTrigger
                value="journeys"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent px-6 py-3 text-sm uppercase tracking-widest font-mono"
                data-testid="tab-journeys"
              >
                <Globe className="w-4 h-4 mr-2" />
                Global Journeys
              </TabsTrigger>
              <TabsTrigger
                value="meetups"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent px-6 py-3 text-sm uppercase tracking-widest font-mono"
                data-testid="tab-meetups"
              >
                <Users className="w-4 h-4 mr-2" />
                Local Meetups
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Journeys Tab */}
          <TabsContent value="journeys">
            <div className="flex justify-end mb-6">
              <Dialog open={showJourneyDialog} onOpenChange={setShowJourneyDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary inline-flex items-center gap-2" data-testid="create-journey-btn">
                    <Plus className="w-4 h-4" />
                    Post Journey
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-ivory border-border max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-h3 text-charcoal">Post a Journey</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div className="form-group">
                      <label className="form-label">Journey Title *</label>
                      <Input
                        value={journeyForm.title}
                        onChange={(e) => setJourneyForm({ ...journeyForm, title: e.target.value })}
                        placeholder="e.g., Solo Trek in Nepal"
                        className="input-luxury"
                        data-testid="journey-title-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Destination *</label>
                      <Input
                        value={journeyForm.destination}
                        onChange={(e) => setJourneyForm({ ...journeyForm, destination: e.target.value })}
                        placeholder="e.g., Kathmandu, Nepal"
                        className="input-luxury"
                        data-testid="journey-destination-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description *</label>
                      <Textarea
                        value={journeyForm.description}
                        onChange={(e) => setJourneyForm({ ...journeyForm, description: e.target.value })}
                        placeholder="Describe your journey plans..."
                        className="input-luxury min-h-[100px] resize-none"
                        data-testid="journey-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label">Start Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal border-b border-muted rounded-none px-0" data-testid="journey-start-date">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {journeyForm.start_date ? format(journeyForm.start_date, "PPP") : "Pick date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-white">
                            <Calendar
                              mode="single"
                              selected={journeyForm.start_date}
                              onSelect={(date) => setJourneyForm({ ...journeyForm, start_date: date })}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="form-group">
                        <label className="form-label">End Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal border-b border-muted rounded-none px-0" data-testid="journey-end-date">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {journeyForm.end_date ? format(journeyForm.end_date, "PPP") : "Pick date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-white">
                            <Calendar
                              mode="single"
                              selected={journeyForm.end_date}
                              onSelect={(date) => setJourneyForm({ ...journeyForm, end_date: date })}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Looking for (spots)</label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={journeyForm.max_companions}
                        onChange={(e) => setJourneyForm({ ...journeyForm, max_companions: parseInt(e.target.value) })}
                        className="input-luxury"
                        data-testid="journey-companions-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Interests (comma-separated)</label>
                      <Input
                        value={journeyForm.interests}
                        onChange={(e) => setJourneyForm({ ...journeyForm, interests: e.target.value })}
                        placeholder="e.g., Hiking, Photography, Culture"
                        className="input-luxury"
                        data-testid="journey-interests-input"
                      />
                    </div>
                    <Button
                      onClick={handleCreateJourney}
                      disabled={!journeyForm.title || !journeyForm.destination || !journeyForm.description}
                      className="btn-primary w-full"
                      data-testid="submit-journey-btn"
                    >
                      Post Journey
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <p className="text-caption">Loading journeys...</p>
              </div>
            ) : journeys.length === 0 ? (
              <div className="text-center py-16">
                <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-body-lg">No journeys posted yet.</p>
                <p className="text-caption mt-2">Be the first to share your travel plans!</p>
              </div>
            ) : (
              <div className="bento-grid">
                {journeys.map((journey) => (
                  <JourneyCard key={journey.journey_id} journey={journey} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Meetups Tab */}
          <TabsContent value="meetups">
            <div className="flex justify-end mb-6">
              <Dialog open={showMeetupDialog} onOpenChange={setShowMeetupDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary inline-flex items-center gap-2" data-testid="create-meetup-btn">
                    <Plus className="w-4 h-4" />
                    Host Meetup
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-ivory border-border max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-h3 text-charcoal">Host a Meetup</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div className="form-group">
                      <label className="form-label">Meetup Title *</label>
                      <Input
                        value={meetupForm.title}
                        onChange={(e) => setMeetupForm({ ...meetupForm, title: e.target.value })}
                        placeholder="e.g., Coffee & Conversation"
                        className="input-luxury"
                        data-testid="meetup-title-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">City/Location *</label>
                      <Input
                        value={meetupForm.location}
                        onChange={(e) => setMeetupForm({ ...meetupForm, location: e.target.value })}
                        placeholder="e.g., Mumbai"
                        className="input-luxury"
                        data-testid="meetup-location-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Venue Type *</label>
                      <Input
                        value={meetupForm.venue_type}
                        onChange={(e) => setMeetupForm({ ...meetupForm, venue_type: e.target.value })}
                        placeholder="e.g., Coffee Shop, Restaurant, Park"
                        className="input-luxury"
                        data-testid="meetup-venue-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description *</label>
                      <Textarea
                        value={meetupForm.description}
                        onChange={(e) => setMeetupForm({ ...meetupForm, description: e.target.value })}
                        placeholder="What's the meetup about?"
                        className="input-luxury min-h-[100px] resize-none"
                        data-testid="meetup-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label">Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal border-b border-muted rounded-none px-0" data-testid="meetup-date">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {meetupForm.date ? format(meetupForm.date, "PPP") : "Pick date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-white">
                            <Calendar
                              mode="single"
                              selected={meetupForm.date}
                              onSelect={(date) => setMeetupForm({ ...meetupForm, date: date })}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Time</label>
                        <Input
                          value={meetupForm.time}
                          onChange={(e) => setMeetupForm({ ...meetupForm, time: e.target.value })}
                          placeholder="e.g., 5:00 PM"
                          className="input-luxury"
                          data-testid="meetup-time-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max Attendees</label>
                      <Input
                        type="number"
                        min="2"
                        max="20"
                        value={meetupForm.max_attendees}
                        onChange={(e) => setMeetupForm({ ...meetupForm, max_attendees: parseInt(e.target.value) })}
                        className="input-luxury"
                        data-testid="meetup-attendees-input"
                      />
                    </div>
                    <Button
                      onClick={handleCreateMeetup}
                      disabled={!meetupForm.title || !meetupForm.location || !meetupForm.description || !meetupForm.venue_type}
                      className="btn-primary w-full"
                      data-testid="submit-meetup-btn"
                    >
                      Host Meetup
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <p className="text-caption">Loading meetups...</p>
              </div>
            ) : meetups.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-body-lg">No meetups scheduled yet.</p>
                <p className="text-caption mt-2">Host the first local gathering!</p>
              </div>
            ) : (
              <div className="bento-grid">
                {meetups.map((meetup) => (
                  <MeetupCard key={meetup.meetup_id} meetup={meetup} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
